import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    RequestStackInformationDialogComponent,
    StackInformationDialogComponent,
} from '@data-terminal/feature-dialogs/dialogs';
import { JobFlowResponse } from '@data-terminal/shared-models/job-flow';
import { catchError, map, Observable, of, take, tap } from 'rxjs';
import { JobFlowApiService } from './job-flow-api.service';

export interface StackInformationDialogData {
    jobFlow: JobFlowResponse;
    noStackData: boolean;
    selectedWorkStepId?: string;
}

@Injectable({ providedIn: 'root' })
export class StackInformationDialogService {
    readonly #dialog = inject(MatDialog);
    readonly #jobFlowApi = inject(JobFlowApiService);

    public openRequestStackInformationDialog(): void {
        this.#dialog.open(RequestStackInformationDialogComponent);
    }

    public openStackInformationDialog(jobFlow: JobFlowResponse, selectedWorkStepId?: string): void {
        this.#dialog.open(StackInformationDialogComponent, {
            maxWidth: '95vw',
            maxHeight: '95vh',
            width: '95vw',
            height: '95vh',
            autoFocus: false,
            disableClose: true,
            data: {
                jobFlow,
                noStackData: !this.#hasWorkSteps(jobFlow),
                selectedWorkStepId,
            },
        });
    }

    public requestStackInformation(
        jobId: string,
        selectedWorkStepId?: string
    ): Observable<JobFlowResponse | undefined> {
        return this.#jobFlowApi.getJobFlow(jobId).pipe(
            take(1),
            map((jobFlow) => ({
                ...jobFlow,
                workSteps: jobFlow.workSteps.filter((step) => step.predecessors?.length || step.successors?.length),
            })),
            tap((jobFlow) => this.openStackInformationDialog(jobFlow, selectedWorkStepId)),
            catchError(() => of(undefined))
        );
    }

    #hasWorkSteps(flow: JobFlowResponse): boolean {
        return (flow.workSteps?.length ?? 0) > 0;
    }
}
